<template>
    <component :is="inline ? 'div' : 'v-dialog'" v-bind="dialogProps" v-on="dialogListeners">
        <component :is="inline ? 'div' : 'v-card'" :class="inline ? '' : 'premium-feature-gate-card d-flex flex-column'">
            <component :is="inline ? 'div' : 'v-card-text'" :class="inline ? '' : 'premium-feature-gate-body flex-grow-1 pa-0 overflow-y-auto'">
                <div class="premium-feature-gate pa-8">
                    <div class="text-center mb-4">
                        <div class="icon-container mb-4">
                            <div class="icon-wrapper animate-float">
                                <div class="premium-gradient icon-background">
                                    <v-icon size="56" color="white">mdi-check-circle</v-icon>
                                </div>
                                <div class="icon-badge icon-badge-star">
                                    <v-icon size="20" color="yellow darken-2">mdi-star</v-icon>
                                </div>
                                <div class="icon-badge icon-badge-chart">
                                    <v-icon size="20" color="primary">mdi-chart-line</v-icon>
                                </div>
                            </div>
                        </div>
                        <h2 class="text-h5 font-weight-bold mb-2">
                            {{ $translate('premiumFeatureTitle') }}
                            <span class="premium-gradient-text">{{ $translate('premium') }}</span>
                        </h2>
                        <div class="premium-feature-gate-content text-center mb-4">
                            <slot name="content">
                                <p class="text-body-2 text--secondary max-width-lg mx-auto">
                                    {{ $translate('premiumFeatureDescription') }}
                                </p>
                            </slot>
                        </div>
                    </div>

                    <div class="text-center mb-8">
                        <v-btn
                            color="primary"
                            class="px-6 py-2 font-weight-bold rounded-pill"
                            :elevation="4"
                            @click="discoverOffers"
                        >
                            {{ $translate('discoverOffers') }}
                            <v-icon class="ml-2" size="18">mdi-arrow-forward</v-icon>
                        </v-btn>
                    </div>

                    <div class="contact-card-wrapper">
                        <div class="contact-card">
                            <div class="contact-label">{{ $translate('haveQuestion') }}</div>
                            <div class="contact-divider"></div>
                            <div class="contact-links">
                                <a :href="`mailto:${contactEmail}`" class="contact-link">
                                    <v-icon size="14" class="mr-1">mdi-email-outline</v-icon>
                                    {{ contactEmail }}
                                </a>
                                <a v-if="contactPhone" :href="`tel:${contactPhone}`" class="contact-link">
                                    <v-icon size="14" class="mr-1">mdi-phone-outline</v-icon>
                                    {{ formattedPhone }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </component>
            <v-card-actions v-if="!inline" class="justify-end pa-4">
                <v-btn text @click="dialogVisible = false">
                    {{ $translate('close') }}
                </v-btn>
            </v-card-actions>
        </component>
    </component>
</template>

<script>
import GlobalMixins from "@/mixins/global";

export default {
    name: "PremiumFeatureGate",
    mixins: [GlobalMixins],
    props: {
        value: {
            type: Boolean,
            default: true,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        requiredSubscription: {
            type: String,
            default: "premium",
            validator: (value) => ["standard", "premium", "classic"].includes(value),
        },
        contactEmail: {
            type: String,
            default: "contact@safetee.io",
        },
        contactPhone: {
            type: String,
            default: "+33972195305",
        },
    },
    computed: {
        dialogVisible: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit("input", val);
            },
        },
        dialogProps() {
            if (this.inline) return {};
            return {
                value: this.dialogVisible,
                "content-class": "premium-feature-gate-dialog",
                "max-width": "720",
            };
        },
        dialogListeners() {
            if (this.inline) return {};
            return {
                input: this.onDialogInput,
            };
        },
        formattedPhone() {
            if (!this.contactPhone) return null;
            // Format +33972195305 to +33 9 72 19 53 05
            const phone = this.contactPhone.replace(/\s/g, "");
            if (phone.startsWith("+33")) {
                return `+33 ${phone.slice(3, 4)} ${phone.slice(4, 6)} ${phone.slice(6, 8)} ${phone.slice(8, 10)} ${phone.slice(10, 12)}`;
            }
            return this.contactPhone;
        },
    },
    methods: {
        onDialogInput(visible) {
            if (!visible) {
                this.hideModal();
            }
        },
        sendMail() {
            window.open(`mailto:${this.contactEmail}`);
        },
        discoverOffers() {
            window.open("https://www.safetee.io/tarifs", "_blank");
        },
    },
    i18n: {
        messages: {
            en: {
                premiumFeatureTitle: "This is a Premium",
                premium: "feature",
                premiumFeatureDescription: "This feature is available with a Premium subscription. Discover our offers and pricing to unlock more capabilities.",
                discoverOffers: "DISCOVER OUR OFFERS",
                haveQuestion: "Have a question?",
                contactUs: "Contact us",
                close: "Close",
            },
            fr: {
                premiumFeatureTitle: "Ceci est une fonctionnalité",
                premium: "Premium",
                premiumFeatureDescription: "Cette fonctionnalité est disponible avec un abonnement Premium. Découvrez nos offres et tarifs pour débloquer davantage de possibilités.",
                discoverOffers: "DÉCOUVRIR NOS OFFRES",
                haveQuestion: "Une question ?",
                contactUs: "Contactez-nous",
                close: "Fermer",
            },
        },
    },
};
</script>

<style lang="scss">
.premium-feature-gate-dialog {
    max-height: 90vh;
}

.premium-feature-gate-card {
    max-height: 90vh;
}

.premium-feature-gate-body {
    max-height: 70vh;
}
</style>

<style scoped lang="scss">
.premium-feature-gate {
    position: relative;
    overflow: hidden;
    background: #fff;


    > * {
        position: relative;
        z-index: 1;
    }

    .icon-container {
        display: flex;
        justify-content: center;
    }

    .icon-wrapper {
        position: relative;
        display: inline-block;
    }

    .icon-background {
        padding: 16px;
        border-radius: 28px;
        box-shadow: 0 12px 20px -5px rgba(99, 102, 241, 0.25);
    }

    .icon-badge {
        position: absolute;
        background: white;
        padding: 6px;
        border-radius: 10px;
        box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.05);

        &.icon-badge-star {
            top: -12px;
            right: -12px;
        }

        &.icon-badge-chart {
            bottom: -6px;
            left: -18px;
        }
    }

    .premium-gradient {
        background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
    }

    .premium-gradient-text {
        background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }

    .contact-card-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
    }

    .contact-card {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        padding: 12px 20px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.7);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(0, 0, 0, 0.1);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        width: auto;

        @media (min-width: 960px) {
            flex-direction: row;
        }

        .contact-label {
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: rgba(0, 0, 0, 0.5);
        }

        .contact-divider {
            display: none;
            width: 1px;
            height: 14px;
            background: rgba(0, 0, 0, 0.1);

            @media (min-width: 960px) {
                display: block;
            }
        }

        .contact-links {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 16px;
        }

        .contact-link {
            display: flex;
            align-items: center;
            color: #6366f1;
            text-decoration: none;
            font-weight: 500;
            font-size: 13px;
            transition: opacity 0.2s;

            &:hover {
                opacity: 0.8;
                text-decoration: underline;
            }
        }
    }

    .max-width-lg {
        max-width: 400px;
    }

    // Bullet list style for slot content (use class premium-gate-bullets on ul, premium-gate-bullet-icon on icon wrapper)
    .premium-feature-gate-content {
        ::v-deep .premium-gate-bullets {
            list-style: none;
            margin: 0 auto;
            max-width: 420px;
            text-align: left;
            padding: 10px 0;

            li {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 10px;
                font-size: 14px;
                line-height: 1.45;
                color: rgba(0, 0, 0, 0.7);
            }

            .premium-gate-bullet-icon {
                flex-shrink: 0;
                width: 22px;
                height: 22px;
                border-radius: 50%;
                background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                margin-top: 2px;
            }
        }

        ::v-deep .premium-gate-content-intro {
            text-align: center;
        }
    }
}

@keyframes float {
    0% {
        transform: translateY(0px);
    }
    50% {
        transform: translateY(-8px);
    }
    100% {
        transform: translateY(0px);
    }
}

.animate-float {
    animation: float 6s ease-in-out infinite;
}

// Dark theme support
.theme--dark {
    .premium-feature-gate {
        background: #1e293b;

        .icon-badge {
            background: #1e293b;
            border-color: rgba(255, 255, 255, 0.1);
        }

        .contact-card {
            background: rgba(30, 41, 59, 0.7);
            border-color: rgba(255, 255, 255, 0.1);

            .contact-label {
                color: rgba(255, 255, 255, 0.5);
            }

            .contact-divider {
                background: rgba(255, 255, 255, 0.1);
            }
        }

        .premium-feature-gate-content ::v-deep .premium-gate-bullets li {
            color: rgba(255, 255, 255, 0.85);
        }
    }
}
</style>
